import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { startCase } from 'lodash'
import { ChevronDown, ChevronUp, ChevronsUpDown, Filter as FilterIcon, XIcon } from 'lucide-react'
import { Badge, Button, Col, DefaultPopover, FormGroup, FormSectionCollapsible, Input, Label, Row, Select } from 'twyne-new.ui'

import DefaultTableResize from '@/components/DefaultTableResize'
import { TruncateWithTooltipFormatter } from '@/components/Grid/components/Table/formatters'
import InfoTip from '@/components/InfoTip'
import DetailsModal from '@/components/modals/DetailsModal'
import { formatters } from 'components/hierarchical-report/utils'
import { ACTIONS_OPTIONS } from 'modules/offer/views/allocation/views/transaction/components/Grid/useColumns/ActionFormatter'
import { colorizeNumberFormatter } from 'modules/offer/views/allocation/views/transaction/components/Grid/useColumns/ColorizeNumberFormatter'
import { formatDate, formatDateTime } from 'utils'

import { useLineChartActions } from './LineChart/context'

const ACTION_BADGE_COLORS = {
  allowed: 'success',
  denied: 'danger',
  created: 'accent',
  reset: 'warning',
  revenue: 'success',
  revenue_cor: 'warning',
  manual: 'accent',
  suppressed: 'danger',
}

function ActionBadge({ action }) {
  const label = ACTIONS_OPTIONS[action]
  if (!label) return '--'

  return (
    <Badge color={ACTION_BADGE_COLORS[action] || 'neutral'} variant="outline" size="sm">
      {label}
    </Badge>
  )
}

function SortableHeader({ children, field, sorting, onSortingChange }) {
  const [sort] = sorting
  const order = sort?.id === field ? sort.desc ? 'desc' : 'asc' : null
  const Icon = {
    asc: ChevronUp,
    desc: ChevronDown,
    default: ChevronsUpDown,
  }[order || 'default']

  const handleClick = () => {
    onSortingChange(currentSorting => {
      const [currentSort] = currentSorting

      if (currentSort?.id !== field) return [{ id: field, desc: false }]
      if (!currentSort.desc) return [{ id: field, desc: true }]
      return []
    })
  }

  return <button type="button" className="flex items-center gap-1.5" onClick={handleClick}>
    {children}
    <Icon size={10} className='twy-text-base-neutral hover:twy-text-base-accent' />
  </button>
}

function PublisherCell({ row }) {
  const id = row.publisher_id
  const name = row.publisher_name

  if (!id) return '--'

  return (
    <Link to={`/offer/publishers/${id}`} target="_blank" rel="noopener noreferrer" className="twy-text-base-accent hover:underline">
      {name ? `[#${id}] - ${name}` : `[#${id}]`}
    </Link>
  )
}

const ACCOUNT_LOG_FORMATTERS = {
  value: value => value != null && value !== '' ? colorizeNumberFormatter(value) : '--',
  created_at: value => value ? formatDateTime(value) : '--',
  updated_at: value => value ? formatDateTime(value) : '--',
  referDate: value => value ? formatDateTime(value) : '--',
}

function AccountLogSection({ row }) {
  if (!row.account_log_id) return null

  const entries = Object.entries(row.account_log || {})

  return (
    <Col size="12">
      <FormSectionCollapsible
        border
        triggerClass="py-2 px-4 twy-subtle-neutral"
        contentClass="p-4"
        titleClass="text-base pr-2"
        title="Account Log"
      >
        <Row>
          {entries.map(([key, value]) => {
            const formatter = ACCOUNT_LOG_FORMATTERS[key]
            return (
              <Col size="6" key={key}>
                <FormGroup>
                  <Label>{startCase(key)}</Label>
                  <span className="twy-text-base-accent">{formatter ? formatter(value) : value ?? '--'}</span>
                </FormGroup>
              </Col>
            )
          })}
        </Row>
      </FormSectionCollapsible>
    </Col>
  )
}

function DetailsAction({ row }) {
  return (
    <DetailsModal title="Bucket Transaction Details">
      {DETAIL_COLUMNS.map(({ field, label, formatter }) => {
        let value = row[field]
        if (formatter) value = formatter(value, row)

        return (
          <Col size="6" key={field}>
            <FormGroup>
              <Label>{label}</Label>
              <span className="twy-text-base-accent">{value ?? '--'}</span>
            </FormGroup>
          </Col>
        )
      })}
      <AccountLogSection row={row} />
    </DetailsModal>
  )
}

const DETAIL_COLUMNS = [
  { label: 'Date', field: 'ref_date', formatter: value => value ? formatDateTime(value) : '--' },
  { label: 'Action', field: 'action', formatter: value => <ActionBadge action={value} /> },
  {
    label: 'Publisher',
    field: 'publisher_id',
    formatter: (_, row) => <PublisherCell row={row} />,
  },
  {
    label: 'Allocation',
    field: 'allocation_id',
    formatter: (id, row) => {
      if (!id) return '--'
      const name = row.allocation_name
      return (
        <Link to={`/offer/allocations/${id}`} target="_blank" rel="noopener noreferrer" className="twy-text-base-accent hover:underline">
          {name ? `[#${id}] - ${name}` : `[#${id}]`}
        </Link>
      )
    },
  },
  { label: 'SubId', field: 'subid', formatter: value => value || '--' },
  {
    label: 'Transaction Value',
    field: 'transaction_value',
    formatter: value => value != null && value !== '' ? colorizeNumberFormatter(value) : '--',
  },
  {
    label: 'Original Value',
    field: 'original_value',
    formatter: value => value != null && value !== '' ? colorizeNumberFormatter(value) : '--',
  },
  {
    label: 'Previous Balance',
    field: 'bucket_value',
    formatter: value => value != null && value !== '' ? colorizeNumberFormatter(value) : '--',
  },
  { label: 'Pekey', field: 'pekey', formatter: value => value || '--' },
  { label: 'User ID', field: 'user_id', formatter: value => value || '--' },
  { label: 'User Session ID', field: 'usession_id', formatter: value => value || '--' },
  { label: 'Path Session ID', field: 'path_session_id', formatter: value => value || '--' },
  { label: 'Path Action ID', field: 'path_action_id', formatter: value => value || '--' },
  { label: 'Refer Date', field: 'referDate', formatter: value => value ? formatDateTime(value) : '--' },
]

const getColumns = ({ sorting, onSortingChange }) => [
  {
    dataField: 'publisher_id',
    text: 'Publisher',
    formatter: (_, row) => <PublisherCell row={row} />,
  },
  {
    dataField: 'allocation_id',
    text: 'Allocation',
    formatter: (id, row) => {
      if (!id) return '--'
      const name = row.allocation_name
      return (
        <Link to={`/offer/allocations/${id}`} className="twy-text-base-accent hover:underline">
          {name ? `[#${id}] - ${name}` : `[#${id}]`}
        </Link>
      )
    },
  },
  {
    dataField: 'subid',
    text: <SortableHeader field="subid" sorting={sorting} onSortingChange={onSortingChange}>SubID</SortableHeader>,
    formatter: cell => <TruncateWithTooltipFormatter value={cell || '--'} />,
  },
  {
    dataField: 'ref_date',
    text: <SortableHeader field="ref_date" sorting={sorting} onSortingChange={onSortingChange}>Transaction Date</SortableHeader>,
    formatter: cell => cell ? formatDateTime(cell) : '--',
    size: 200,
  },
  {
    dataField: 'action',
    text: 'Action',
    formatter: cell => <ActionBadge action={cell} />,
  },
  {
    dataField: 'transaction_value',
    text: <SortableHeader field="transaction_value" sorting={sorting} onSortingChange={onSortingChange}>
      <Label>Transaction Value <InfoTip>The value of the transaction.</InfoTip></Label>
    </SortableHeader>,
    formatter: cell => {
      if (cell == null || cell === '') return '--'
      return formatters.colorize(formatters.money()(Number(cell)), cell)
    },
  },
  {
    dataField: 'original_value',
    text: <SortableHeader field="original_value" sorting={sorting} onSortingChange={onSortingChange}>
      <Label>Original Value <InfoTip>The transaction value before any adjustment.</InfoTip></Label>
    </SortableHeader>,
    formatter: cell => {
      if (cell == null || cell === '') return '--'
      return formatters.colorize(formatters.money()(Number(cell)), cell)
    },
  },
  {
    dataField: 'bucket_value',
    text: <SortableHeader field="bucket_value" sorting={sorting} onSortingChange={onSortingChange}>
      <Label>Previous Balance <InfoTip>The previous balance of the bucket before the transaction.</InfoTip></Label>
    </SortableHeader>,
    formatter: cell => {
      if (cell == null || cell === '') return '--'
      return formatters.colorize(formatters.money()(Number(cell)), cell)
    },
  },
  {
    dataField: 'id',
    text: '',
    formatter: (_, row) => <DetailsAction row={row} />,
  },
]

const ACTION_OPTIONS = Object.entries(ACTIONS_OPTIONS).map(([value, label]) => ({ value, label }))

const VALUE_OPERATORS = [
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
]

const EMPTY_FILTERS = { action: null, operator: null, value: '' }

const FILTER_RESET = {
  action: { action: null },
  value: { operator: null, value: '' },
}

function TransactionsFilter({ filters, onApply }) {
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState(filters)

  const handleOpenChange = nextOpen => {
    if (nextOpen) setPending(filters)
    setOpen(nextOpen)
  }

  const handleApply = () => {
    onApply(pending)
    setOpen(false)
  }

  return (
    <DefaultPopover
      title="Filter Transactions"
      trigger={<Button variant="outline" size="lg"><FilterIcon size={16} /></Button>}
      wrapperProps={{ open, onOpenChange: handleOpenChange }}
      contentProps={{ className: 'w-80' }}
    >
      <div className="flex flex-col gap-4">
        <FormGroup>
          <Label>Action</Label>
          <Select
            options={ACTION_OPTIONS}
            placeholder="Select action..."
            clearable
            value={pending.action}
            onChange={option => setPending(state => ({ ...state, action: option?.value || null }))}
          />
        </FormGroup>
        <FormGroup>
          <Label>Transaction Value</Label>
          <div className="flex gap-2">
            <Select
              className="w-20 shrink-0"
              options={VALUE_OPERATORS}
              placeholder="op"
              value={pending.operator}
              onChange={option => setPending(state => ({ ...state, operator: option?.value || null }))}
            />
            <Input
              type="number"
              placeholder="Value"
              value={pending.value}
              onChange={e => setPending(state => ({ ...state, value: e.target.value }))}
            />
          </div>
        </FormGroup>
        <Button block color="accent" onClick={handleApply}>Apply</Button>
      </div>
    </DefaultPopover>
  )
}

function AppliedFilters({ filters, dateFilter, onRemove }) {
  const tags = []

  if (dateFilter) {
    tags.push({ key: 'date', label: `Date: ${dateFilter.label || dateFilter}` })
  }

  if (filters.action) {
    tags.push({ key: 'action', label: `Action: ${ACTIONS_OPTIONS[filters.action] || filters.action}` })
  }

  if (filters.operator && filters.value !== '' && filters.value != null) {
    const symbol = VALUE_OPERATORS.find(({ value }) => value === filters.operator)?.label || filters.operator
    tags.push({ key: 'value', label: `Value ${symbol} ${filters.value}` })
  }

  if (!tags.length) return null

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      {tags.map(({ key, label }) => <Badge key={key} variant="light" size="lg">
        <div className="flex items-center gap-2">
          {label}
          <XIcon size="12" className="cursor-pointer hover:twy-text-base-accent" onClick={() => onRemove(key)} />
        </div>
      </Badge>)}
    </div>
  )
}

export default function DocumentsTable({ data, sorting, onSortingChange }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const { actions: { dateFilter, setDateFilter } } = useLineChartActions()

  const filteredRows = useMemo(() => {
    const rows = Array.isArray(data) ? data : []

    return rows.filter(row => {
      if (filters.action && row.action !== filters.action) return false

      if (filters.operator && filters.value !== '' && filters.value != null) {
        const value = Number(row.transaction_value)
        const filterValue = Number(filters.value)
        if (Number.isNaN(value) || Number.isNaN(filterValue)) return false
        if (filters.operator === 'gt' && !(value > filterValue)) return false
        if (filters.operator === 'lt' && !(value < filterValue)) return false
      }

      if (dateFilter) {
        const refDate = new Date(row.ref_date).getTime()
        const { start, end } = dateFilter

        if (Number.isFinite(start) && Number.isFinite(end)) {
          if (refDate < start || refDate >= end) return false
        } else if (formatDate(row.ref_date, 'MM/DD') !== dateFilter) {
          return false
        }
      }

      return true
    })
  }, [data, filters, dateFilter])

  const handleRemoveFilter = key => {
    if (key === 'date') {
      setDateFilter(null)
      return
    }

    setFilters(state => ({ ...state, ...FILTER_RESET[key] }))
  }

  return (
    <div>
      <div className="flex justify-end mb-3">
        <TransactionsFilter filters={filters} onApply={setFilters} />
      </div>
      <AppliedFilters filters={filters} dateFilter={dateFilter} onRemove={handleRemoveFilter} />
      <DefaultTableResize
        keyField="id"
        data={filteredRows}
        columns={getColumns({ sorting, onSortingChange })}
        sticky={false}
        noDataIndication={() => 'No transactions to show.'}
      />
    </div>
  )
}
